import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { advertImgUrl, assetUrl, fetchAdverts, resolveAdLink, adForSpace } from "@/lib/glossyApi";
import { ArrowUpRight } from "lucide-react";

function AdLink({ ad, className, children, testid }) {
  const { href, external } = resolveAdLink(ad);
  if (external) return <a href={href} target="_blank" rel="noreferrer" className={className} data-testid={testid}>{children}</a>;
  return <Link to={href} className={className} data-testid={testid}>{children}</Link>;
}

// Proper small ad units in FIXED spaces — one member holds a space for a full edition.
// Empty space -> collapses (nothing shown). variant: "square" | "banner". mode: "auto" | "image" | "branded".
export default function MiniAd({ variant = "square", mode = "auto", excludeMemberId = null, seed = 0, className = "", space = "home_square" }) {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    fetchAdverts().then((list) => setAd(adForSpace(list, space, excludeMemberId))).catch(() => {});
  }, [excludeMemberId, space, variant]);

  if (!ad) return null;
  const a = ad.advert;
  const logo = assetUrl(ad.logo) || ad.fallback_logo;
  const accent = ad.accent || "#141414";
  const useImage = mode === "image" || (mode === "auto" && seed % 2 === 0);

  if (variant === "banner") {
    return (
      <AdLink ad={ad} className={`group block relative overflow-hidden ${className}`} testid="mini-ad-banner">
        <div className="relative flex items-center gap-5 sm:gap-8 border border-ink/15 text-cream px-5 sm:px-8 py-5 grain" style={{ backgroundColor: accent }}>
          {logo && <img src={logo} alt={ad.business_name} className="h-8 sm:h-10 w-auto object-contain shrink-0 relative z-10" />}
          <div className="min-w-0 flex-1 relative z-10">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-cream/50">Advertisement</p>
            <p className="font-serif text-lg sm:text-2xl leading-tight tracking-tight truncate">{a.headline}</p>
            <p className="font-sans text-xs sm:text-sm text-cream/70 truncate">{ad.business_name} · {ad.borough}</p>
          </div>
          <span className="hidden sm:inline-flex items-center gap-2 border border-cream/60 px-5 py-2.5 overline shrink-0 relative z-10 group-hover:bg-cream group-hover:text-ink transition-colors">
            {a.cta || "Discover"} <ArrowUpRight size={13} />
          </span>
        </div>
      </AdLink>
    );
  }

  // square
  return (
    <AdLink ad={ad} className={`group block relative overflow-hidden aspect-square ed-frame ${className}`} testid="mini-ad-square">
      {useImage ? (
        <>
          <img src={advertImgUrl(ad)} alt={ad.business_name} className="absolute inset-0 w-full h-full object-cover ed-grade transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/90" />
          <div className="absolute top-0 left-0 right-0 flex items-start justify-between px-4 pt-4 z-10">
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-cream/70">Advertisement</span>
            {logo && <span className="bg-ink/70 px-2 py-1"><img src={logo} alt="" className="h-5 w-auto object-contain" /></span>}
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
            <p className="font-serif text-cream text-2xl sm:text-3xl leading-none tracking-tight">{a.headline}</p>
            <span className="inline-flex items-center gap-2 mt-4 border border-cream/70 px-4 py-2 overline text-cream group-hover:bg-cream group-hover:text-ink transition-colors">
              {a.cta || "Discover"} <ArrowUpRight size={12} />
            </span>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 text-cream flex flex-col p-6 grain overflow-hidden" style={{ backgroundColor: accent }}>
          {/* oversized initial graphic */}
          <span className="absolute -bottom-8 -right-4 z-0 font-serif leading-none select-none text-cream/10" style={{ fontSize: "12rem" }}>{(ad.business_name || "S")[0]}</span>
          <div className="relative z-10 flex items-center justify-between">
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream/50">Advertisement</span>
            {logo && <img src={logo} alt={ad.business_name} className="h-6 w-auto object-contain opacity-90" />}
          </div>
          <div className="relative z-10 flex-1 flex flex-col justify-center py-3">
            <p className="font-serif text-2xl sm:text-3xl leading-[1.02] tracking-tight line-clamp-3">{a.headline}</p>
          </div>
          <div className="relative z-10 flex items-end justify-between gap-3">
            <span className="inline-flex items-center gap-2 border border-cream/60 px-4 py-2.5 overline group-hover:bg-cream group-hover:text-ink transition-colors">
              {a.cta || "Discover"} <ArrowUpRight size={12} />
            </span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-cream/50 text-right">{ad.business_name}</span>
          </div>
        </div>
      )}
    </AdLink>
  );
}
